import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/types/database.types";

export interface PlanLimitError {
  message: string;
  limit: number;
  current: number;
}

export type PlanLimitType =
  | "active_agents"
  | "conversations"
  | "knowledge_items"
  | "files"
  | "storage";

export async function checkPlanLimits(
  organizationId: string,
  limitType?: PlanLimitType,
  fileSizeMB?: number, // Only needed for storage check
): Promise<PlanLimitError | null> {
  // Get organization and its plan (using current schema until migration is complete)
  const { data: organization, error: orgError } = await supabase
    .from("organizations")
    .select(
      `
      *,
      plans!inner (
        *,
        plan_features!inner (*)
      )
    `,
    )
    .eq("id", organizationId)
    .single();

  if (orgError || !organization) {
    throw new Error("Failed to fetch organization data");
  }

  const plan = organization.plans;
  if (!plan || !plan.plan_features || plan.plan_features.length === 0) {
    throw new Error("Plan features not found for organization");
  }
  const features = plan.plan_features[0];

  // Only check the requested limit, or all if not specified
  if (!limitType || limitType === "active_agents") {
    console.log("🔍 Plan limits debug:", {
      organizationId,
      max_active_agents: features.max_active_agents,
      active_agents_count: organization.active_agents_count,
      isNull: features.max_active_agents === null,
    });

    if (features.max_active_agents !== null) {
      const activeAgents = organization.active_agents_count || 0;
      if (activeAgents >= features.max_active_agents) {
        return {
          message: `You have reached the maximum number of active agents (${features.max_active_agents}) for your plan.`,
          limit: features.max_active_agents,
          current: activeAgents,
        };
      }
    }
  }

  if (!limitType || limitType === "conversations") {
    if (features.max_conversations_per_month !== null) {
      const conversationsThisMonth = organization.conversations_this_month || 0;
      if (conversationsThisMonth >= features.max_conversations_per_month) {
        return {
          message: `You have reached the maximum number of monthly conversations (${features.max_conversations_per_month}) for your plan.`,
          limit: features.max_conversations_per_month,
          current: conversationsThisMonth,
        };
      }
    }
  }

  if (!limitType || limitType === "knowledge_items") {
    if (features.max_knowledge_items !== null) {
      const knowledgeItems = organization.knowledge_items_count || 0;
      if (knowledgeItems >= features.max_knowledge_items) {
        return {
          message: `You have reached the maximum number of knowledge items (${features.max_knowledge_items}) for your plan.`,
          limit: features.max_knowledge_items,
          current: knowledgeItems,
        };
      }
    }
  }

  if (!limitType || limitType === "files") {
    if (features.max_files !== null) {
      const files = organization.files_count || 0;
      if (files >= features.max_files) {
        return {
          message: `You have reached the maximum number of files (${features.max_files}) for your plan.`,
          limit: features.max_files,
          current: files,
        };
      }
    }
  }

  if (!limitType || limitType === "storage") {
    if (features.max_storage_mb !== null) {
      const storageUsage = organization.storage_usage_mb || 0;
      // If fileSizeMB is provided, check if the new file would exceed the limit
      const totalStorage = fileSizeMB
        ? storageUsage + fileSizeMB
        : storageUsage;
      if (totalStorage > features.max_storage_mb) {
        return {
          message: `You have reached the maximum storage limit (${features.max_storage_mb}MB) for your plan.`,
          limit: features.max_storage_mb,
          current: totalStorage,
        };
      }
    }
  }

  return null;
}

export async function checkFeatureAvailability(
  organizationId: string,
  feature: keyof Tables<"plan_features">,
): Promise<boolean> {
  // Get organization and its plan (using current schema until migration is complete)
  const { data: organization, error: orgError } = await supabase
    .from("organizations")
    .select(
      `
      *,
      plans!inner (
        *,
        plan_features!inner (*)
      )
    `,
    )
    .eq("id", organizationId)
    .single();

  if (orgError || !organization) {
    throw new Error("Failed to fetch organization data");
  }

  return organization.plans.plan_features[0][feature] === true;
}
